/* Cedarwel Ingredients — no deps, no build step; not needed for first paint.
   Motion touches transform/opacity only; scroll and pointer paints are
   rAF-batched; will-change self-clears; one AbortController per section. */
(() => {
  const REG = new WeakMap();
  const RM = matchMedia('(prefers-reduced-motion: reduce)');
  const ROOT = '[data-cw-ing]';
  const CARD = '[data-cw-ing-card]';
  const q = (el, n) => el.querySelector('[data-cw-ing-' + n + ']');
  const on = (t, n, f, o) => t.addEventListener(n, f, o);
  const raf = f => requestAnimationFrame(f);
  const flipped = c => c.classList.contains('is-flipped');

  const prime = el => {
    if (!el) return;
    el.style.willChange = 'transform, opacity';
    const clear = () => {
      el.style.willChange = '';
      el.style.removeProperty('--cw-ing-delay');
    };
    const done = e => { if (e.target == el) clear(); };
    ['transitionend', 'animationend'].forEach(n => on(el, n, done, { once: true }));
    setTimeout(clear, 1600);
  };

  const setInert = (el, hide) => {
    el?.toggleAttribute('inert', hide);
    el?.setAttribute('aria-hidden', String(hide));
  };

  const setFlip = (card, open) => {
    if (!card || flipped(card) == open) return;
    prime(q(card, 'inner'));
    raf(() => card.classList.toggle('is-flipped', open));
    q(card, 'toggle')?.setAttribute('aria-expanded', String(open));
    setInert(q(card, 'front'), open);
    setInert(q(card, 'back'), !open);
  };

  const watchEntries = (root, cfg) => {
    const cards = root.querySelectorAll(CARD);
    if (cfg.entry == 'none' || !('IntersectionObserver' in window)) {
      cards.forEach(c => c.classList.add('is-inview'));
      return null;
    }
    const io = new IntersectionObserver(es => {
      let k = 0; /* stagger runs per revealed batch, not absolute index */
      for (const en of es) {
        if (!en.isIntersecting) continue;
        const el = en.target;
        io.unobserve(el);
        if (!RM.matches && cfg.stagger) el.style.setProperty('--cw-ing-delay', k++ * cfg.stagger + 'ms');
        prime(el);
        raf(() => el.classList.add('is-inview'));
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    cards.forEach(c => io.observe(c));
    return io;
  };

  const initSlider = (root, cfg, ac) => {
    const t = q(root, 'track');
    if (!t) return;
    const pas = { passive: true, signal: ac.signal };
    const prev = q(root, 'prev');
    const next = q(root, 'next');
    const fill = q(root, 'fill');
    let pend = 0;

    const paint = () => {
      pend = 0;
      const max = t.scrollWidth - t.clientWidth;
      if (fill) fill.style.transform = 'scaleX(' + (max > 2 ? Math.min(t.scrollLeft / max, 1) : 1) + ')';
      prev?.toggleAttribute('data-dim', !cfg.loop && t.scrollLeft < 4);
      next?.toggleAttribute('data-dim', !cfg.loop && t.scrollLeft > max - 4);
      root.classList.toggle('cw-ing--no-overflow', max < 4);
    };
    const queue = () => { if (!pend) pend = raf(paint); };
    on(t, 'scroll', queue, pas);
    on(window, 'resize', queue, pas);
    paint();

    const go = dir => {
      const card = t.querySelector(CARD);
      const gap = parseFloat(getComputedStyle(t).columnGap) || 24;
      const step = (card ? card.getBoundingClientRect().width : t.clientWidth * 0.8) + gap;
      const max = t.scrollWidth - t.clientWidth;
      let x = t.scrollLeft + dir * step;
      if (cfg.loop) { if (x > max + 4) x = 0; else if (x < -4) x = max; }
      t.scrollTo({ left: x, behavior: RM.matches ? 'auto' : 'smooth' });
    };
    prev && on(prev, 'click', () => go(-1), pas);
    next && on(next, 'click', () => go(1), pas);

    if (cfg.drag) {
      let down = false, moved = false, sx = 0, sl = 0, px = 0, drf = 0;
      const apply = () => { drf = 0; t.scrollLeft = sl - (px - sx); };
      on(t, 'pointerdown', e => {
        if (e.pointerType != 'mouse' || e.button) return;
        down = true; moved = false; sx = e.clientX; sl = t.scrollLeft;
      }, pas);
      on(t, 'pointermove', e => {
        if (!down) return;
        px = e.clientX;
        if (!moved && Math.abs(px - sx) > 6) { moved = true; t.classList.add('is-dragging'); }
        if (moved && !drf) drf = raf(apply);
      }, pas);
      const up = () => {
        if (!down) return;
        down = false;
        t.classList.remove('is-dragging');
      };
      ['pointerup', 'pointercancel'].forEach(n => on(t, n, up, pas));
      on(t, 'click', e => {
        if (moved) { moved = false; e.preventDefault(); e.stopPropagation(); }
      }, { capture: true, signal: ac.signal });
    }

    if (cfg.autoplay && !RM.matches) {
      let tm = 0;
      const stop = () => clearTimeout(tm);
      const tick = () => { tm = setTimeout(() => { document.hidden || go(1); tick(); }, cfg.interval); };
      tick();
      if (cfg.pause) {
        ['pointerenter', 'focusin', 'touchstart'].forEach(n => on(t, n, stop, pas));
        ['pointerleave', 'focusout'].forEach(n => on(t, n, () => { stop(); tick(); }, pas));
      }
      on(ac.signal, 'abort', stop);
    }
  };

  const init = root => {
    if (!root || REG.has(root)) return;
    const ac = new AbortController();
    const sig = { signal: ac.signal };
    const d = root.dataset;
    const cfg = {
      entry: d.cwIngEntry || 'none',
      stagger: +d.cwIngStagger || 0,
      drag: d.cwIngDrag == 'true',
      autoplay: d.cwIngAutoplay == 'true',
      interval: (+d.cwIngInterval || 6) * 1000,
      pause: d.cwIngPause == 'true',
      loop: d.cwIngLoop == 'true'
    };
    root.classList.add('cw-ing--js');

    on(root, 'click', e => {
      const btn = e.target.closest('[data-cw-ing-toggle]');
      const card = btn && btn.closest(CARD);
      if (card) setFlip(card, !flipped(card));
    }, sig);

    on(root, 'keydown', e => {
      const card = e.key == 'Escape' && e.target.closest(CARD);
      if (card && flipped(card)) {
        e.stopPropagation();
        setFlip(card, false);
        q(card, 'toggle')?.focus();
      }
    }, sig);

    const io = watchEntries(root, cfg);
    if (d.cwIngLayout == 'slider') initSlider(root, cfg, ac);

    REG.set(root, {
      destroy() { io?.disconnect(); ac.abort(); REG.delete(root); },
      flip(card, open) {
        if (open) card.scrollIntoView({ behavior: RM.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
        setFlip(card, open);
      }
    });
  };

  const boot = () => document.querySelectorAll(ROOT).forEach(init);
  document.readyState == 'loading'
    ? on(document, 'DOMContentLoaded', boot, { once: true })
    : boot();

  /* Theme editor lifecycle */
  on(document, 'shopify:section:load', e => init(e.target.querySelector(ROOT)));
  on(document, 'shopify:section:unload', e => REG.get(e.target.querySelector(ROOT))?.destroy());
  const onBlock = (e, open) => {
    const card = e.target.closest(CARD) || e.target.querySelector(CARD);
    card && REG.get(card.closest(ROOT))?.flip(card, open);
  };
  on(document, 'shopify:block:select', e => onBlock(e, true));
  on(document, 'shopify:block:deselect', e => onBlock(e, false));
})();